import { Helmet } from 'react-helmet-async';
import { Grid, Typography } from '@mui/material';
import './component/style.css';
import {
  getRole,
  getToken,
  removeUserSession,
  getAcl,
  getMenuAccess,
  getFirstAccessibleMenuPath,
} from '../utils/common';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoginButton, LogoutButton } from './component/buttonkeycloak';
import logger from '../utils/logger';

export default function LoginPage() {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();
  const location = useLocation();
  const token = getToken();
  const role = getRole();
  useEffect(() => {
    if (role === 'undefined' && initialized && keycloak.authenticated) {
      sessionStorage.clear();
      sessionStorage.setItem('token', 'gada');
      sessionStorage.setItem('role', 'norole');
    } else if (['maker', 'checker', 'data_owner', 'super_admin', 'data_viewer'].includes(role)) {
      if (sessionStorage.getItem('token') == null) {
        removeUserSession();
        navigate('/login', { replace: true });
      } else {
        // Check if there's a stored redirect path
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        if (redirectPath && redirectPath !== '/login' && redirectPath !== '/welcome') {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath, { replace: true });
        } else {
          // Use dynamic ACL-based redirect instead of hardcoded role
          const determineRedirectPath = async () => {
            try {
              // Wait for permissions to be available
              let acl = getAcl();
              const maxRetries = 15; // Increased retries
              let attempts = 0;

              // Check if ACL is valid (not empty, not null, not empty array string)
              const isValidAcl = (aclValue) => {
                if (!aclValue) return false;
                if (aclValue === '[]' || aclValue === 'null' || aclValue === 'undefined' || aclValue === '')
                  return false;
                try {
                  const parsed = JSON.parse(aclValue);
                  return Array.isArray(parsed) && parsed.length > 0;
                } catch {
                  return false;
                }
              };

              while (!isValidAcl(acl) && attempts < maxRetries) {
                attempts += 1;
                await new Promise((resolve) => setTimeout(resolve, 500));
                acl = getAcl();
              }

              if (!isValidAcl(acl)) {
                try {
                  await getMenuAccess(true);
                  // Wait a bit more for it to be stored
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  acl = getAcl();
                } catch (error) {
                  logger.error('LoginPage: Failed to fetch permissions:', error);
                  navigate('/dashboard/app', { replace: true });
                  return;
                }
              }

              if (!isValidAcl(acl)) {
                logger.warn('LoginPage: ACL still invalid after direct fetch, using fallback');
                navigate('/dashboard/app', { replace: true });
                return;
              }

              // Get first accessible menu path (matches sidebar logic exactly)
              const redirectPath = getFirstAccessibleMenuPath(acl);
              navigate(redirectPath, { replace: true });
            } catch (error) {
              logger.error('Error determining redirect path:', error);
              navigate('/dashboard/app', { replace: true });
            }
          };

          determineRedirectPath();
        }
      }
    }
    if (keycloak.authenticated && location.pathname === '/login' && initialized) {
      if (sessionStorage.getItem('token') == null) {
        removeUserSession();
        navigate('/login', { replace: true });
      } else {
        // Check if there's a stored redirect path
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        if (redirectPath && redirectPath !== '/login' && redirectPath !== '/welcome') {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath, { replace: true });
        } else {
          // Use dynamic ACL-based redirect instead of hardcoded role
          const determineRedirectPath = async () => {
            try {
              // Wait for permissions to be available
              let acl = getAcl();
              const maxRetries = 15; // Increased retries
              let attempts = 0;

              // Check if ACL is valid (not empty, not null, not empty array string)
              const isValidAcl = (aclValue) => {
                if (!aclValue) return false;
                if (aclValue === '[]' || aclValue === 'null' || aclValue === 'undefined' || aclValue === '')
                  return false;
                try {
                  const parsed = JSON.parse(aclValue);
                  return Array.isArray(parsed) && parsed.length > 0;
                } catch {
                  return false;
                }
              };

              while (!isValidAcl(acl) && attempts < maxRetries) {
                attempts += 1;
                await new Promise((resolve) => setTimeout(resolve, 500));
                acl = getAcl();
              }

              if (!isValidAcl(acl)) {
                try {
                  await getMenuAccess(true);
                  // Wait a bit more for it to be stored
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  acl = getAcl();
                } catch (error) {
                  logger.error('LoginPage: Failed to fetch permissions:', error);
                  navigate('/dashboard/app', { replace: true });
                  return;
                }
              }

              if (!isValidAcl(acl)) {
                logger.warn('LoginPage: ACL still invalid after direct fetch, using fallback');
                navigate('/dashboard/app', { replace: true });
                return;
              }

              // Get first accessible menu path (matches sidebar logic exactly)
              const redirectPath = getFirstAccessibleMenuPath(acl);
              navigate(redirectPath, { replace: true });
            } catch (error) {
              logger.error('Error determining redirect path:', error);
              navigate('/dashboard/app', { replace: true });
            }
          };

          determineRedirectPath();
        }
      }
    }
  }, [role, navigate, keycloak.authenticated, initialized, location.pathname]);

  if (!initialized) {
    return null;
  }
  return (
    <>
      <Helmet>
        <title> Login | SMARTSHIELD </title>
      </Helmet>
      <Grid
        xs={12}
        item
        md={6}
        style={{
          backgroundImage: 'url(/assets/Signup.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'absolute',
          height: '100vh',
          zIndex: '1',
        }}
      >
        {''}
        {(() => {
          const hasNoToken = !token;
          const hasInvalidRole = (token && role === 'undefined') || role === 'norole' || role === 'null';

          if (hasNoToken) {
            return (
              <div className="relative z-100 container mx-auto px-4 h-full">
                <div className="relative flex content-center items-center justify-center h-full">
                  <img src="/assets/smartshield.webp" alt="SMARTSHIELD Logo" className="w-100" />

                  <div className="text-center mb-3">
                    <h1 className="text-center text-5xl font-bold">
  Welcome to{" "}
  <span className="
  bg-gradient-to-r
  from-[#3fbac2]
  via-[#2563eb]
  to-[#1e293b]
  bg-clip-text
  text-transparent
">
  SMARTSHIELD
</span>
</h1>
                    <Typography variant="h4" align="center" style={{ color: '#000000', paddingBottom: '30px', fontWeight: 'bold' }}>
                      A Blockchain-based Intelligent Data Integrity System to Protect the Integrity of Your Mission
                      Critical Data.
                    </Typography>
                    <LoginButton />
                    <div style={{ paddingTop: '30px' }}> {token ? <LogoutButton /> : ''}</div>

                    <div className="w-full">
                      <Typography variant="h6" align="center" style={{ color: '#21386d', paddingTop: '30px' }}>
                        {' '}
                        &copy; 2024 ChainSmart - All Rights Reserved
                      </Typography>
                    </div>
                  </div>
                </div>
                <div style={{ width: '100%' }} className="flex-auto px-4 lg:px-10 py-10 pt-0" />
              </div>
            );
          }

          if (hasInvalidRole) {
            return (
              <div className="relative z-100 container mx-auto px-4 h-full">
                <div className="relative flex content-center items-center justify-center h-full">
                  <img src="/assets/smartshield.webp" alt="SMARTSHIELD Logo" className="w-100" />

                  <div className="text-center mb-3">
                     <h1 className="text-center text-5xl font-bold">
  Welcome to{" "}
  <span className="
  bg-gradient-to-r
  from-[#3fbac2]
  via-[#2563eb]
  to-[#1e293b]
  bg-clip-text
  text-transparent
">
  SMARTSHIELD
</span>
</h1>
                    <Typography variant="h3" align="center" style={{ color: '#000000', paddingBottom: '30px' }}>
                      Please contact your administrator to get access to the system.
                    </Typography>
                    <div style={{ paddingTop: '30px' }}> {token ? <LogoutButton /> : ''}</div>

                    <div className="w-full">
                      <Typography variant="h6" align="center" style={{ color: '#21386d', paddingTop: '30px' }}>
                        {' '}
                        &copy; 2024 ChainSmart - All Rights Reserved
                      </Typography>
                    </div>
                  </div>
                </div>
                <div style={{ width: '100%' }} className="flex-auto px-4 lg:px-10 py-10 pt-0" />
              </div>
            );
          }

          // Default case - idle detected
          return (
            <div className="relative z-100 container mx-auto px-4 h-full">
              <div className="relative flex content-center items-center justify-center h-full">
                <img src="/assets/smartshield.webp" alt="SMARTSHIELD Logo" className="w-100" />

                <div className="text-center mb-3">
                   <h1 className="text-center text-5xl font-bold">
  Welcome to{" "}
  <span className="
  bg-gradient-to-r
  from-[#3fbac2]
  via-[#2563eb]
  to-[#1e293b]
  bg-clip-text
  text-transparent
">
  SMARTSHIELD
</span>
</h1>
                  <Typography variant="h3" align="center" style={{ color: '#000000', paddingBottom: '30px' }}>
                    Idle detected, Please login again
                  </Typography>
                  <div style={{ paddingTop: '30px' }}> {token ? <LogoutButton /> : ''}</div>

                  <div className="w-full">
                    <Typography variant="h6" align="center" style={{ color: '#21386d', paddingTop: '30px' }}>
                      {' '}
                      &copy; 2024 ChainSmart - All Rights Reserved
                    </Typography>
                  </div>
                </div>
              </div>
              <div style={{ width: '100%' }} className="flex-auto px-4 lg:px-10 py-10 pt-0" />
            </div>
          );
        })()}
      </Grid>
    </>
  );
}
